import { useState } from 'react';
import { File, FileCode } from 'lucide-react';
import type { CapabilityRevisionFile } from '@/types';
import {
    FileContentDrawer,
    type FileContentDrawerPayload,
} from '@/components/capability-detail/FileContentDrawer';
import {
    buildWorkingCopyManifest,
    type WorkingCopyEntityData,
} from '@/components/capability-detail/RevisionsTab';

const GRID_STYLE = {
    gridTemplateColumns: 'repeat(auto-fill, minmax(265px, 322px))',
    gap: 'clamp(10px, 2vw, 20px)',
} as const;

const CARD_BASE =
    'relative z-0 flex min-h-[140px] w-full flex-col rounded-lg border border-slate-200 border-l-4 bg-white p-3 shadow-sm transition-shadow duration-150 hover:border-slate-300 hover:bg-slate-50';

function isManifestFile(filePath: string, entityType: string): boolean {
    if (!filePath.endsWith('.json')) return false;
    const prefix = entityType + 's/';
    return filePath.startsWith(prefix);
}

interface FilesTabProps {
    files?: CapabilityRevisionFile[];
    entityData?: WorkingCopyEntityData;
    workingRevisionTypeConfig?: Record<string, unknown>;
}

export function FilesTab({ files, entityData, workingRevisionTypeConfig }: FilesTabProps) {
    const [contentDrawer, setContentDrawer] = useState<FileContentDrawerPayload | null>(null);

    if (!files || files.length === 0) {
        return (
            <div className="flex h-64 items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50">
                <div className="flex flex-col items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-slate-100">
                        <File size={24} className="text-slate-400" />
                    </div>
                    <p className="text-[15.4px] text-slate-400">No files attached</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="grid justify-start" style={GRID_STYLE}>
                {files.map((file) => (
                    <div key={file.id} className={`${CARD_BASE} border-l-sky-500`}>
                        <div className="flex min-h-0 w-full flex-1 flex-col">
                            <div className="flex min-h-0 flex-1 flex-col">
                                <div className="flex flex-row items-start gap-2">
                                    <div className="flex shrink-0 items-center justify-center rounded-md bg-sky-100 p-1.5 text-sky-600">
                                        <FileCode size={20} />
                                    </div>
                                    <div className="flex min-w-0 flex-1 flex-col">
                                        <span className="break-all text-left font-mono text-[15.4px] font-bold leading-tight text-slate-800">
                                            {file.path}
                                        </span>
                                        <span className="mt-1 text-left text-[11px] leading-tight text-slate-500">
                                            {file.content_type} &middot; {file.size_bytes} bytes
                                        </span>
                                    </div>
                                </div>
                            </div>
                            {file.sha256 && (
                                <div className="border-t border-slate-100 pt-1.5 text-right font-mono text-[11px] text-slate-400">
                                    {file.sha256.slice(0, 12)}…
                                </div>
                            )}
                            {file.content_text && (
                                <div className="border-t border-slate-100 pt-1.5">
                                    <details>
                                        <summary
                                            className="cursor-pointer text-[11px] text-slate-400 transition-colors hover:text-slate-600"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                let text = file.content_text;
                                                if (!text) return;
                                                if (entityData && workingRevisionTypeConfig && isManifestFile(file.path, entityData.type)) {
                                                    const manifest = buildWorkingCopyManifest({
                                                        entityData,
                                                        typeConfig: workingRevisionTypeConfig,
                                                    });
                                                    text = JSON.stringify(manifest, null, 2);
                                                }
                                                setContentDrawer({
                                                    filePath: file.path,
                                                    content: text,
                                                    contentType: file.content_type,
                                                    sizeBytes: new Blob([text]).size,
                                                });
                                            }}
                                        >
                                            Content
                                        </summary>
                                    </details>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
            <FileContentDrawer
                open={contentDrawer !== null}
                onOpenChange={(open) => {
                    if (!open) setContentDrawer(null);
                }}
                payload={contentDrawer}
            />
        </>
    );
}
